import { useEffect, useState } from 'react';
import { useTranslation } from '../../i18n';
import { api } from '../../lib/api';
import { useSaveIndicator } from '../../hooks/useSaveIndicator';
import { SaveToast } from '../../components/SaveToast';

const MOCK_ALWAYS = ['#F8M', '#Fundacja8Marca', '#Heritage'];
const MOCK_BY_TOPIC = [
  { topic: 'Heritage',       hashtags: ['#CulturalHeritage', '#Dziedzictwo', '#DigitalPreservation'] },
  { topic: 'Sustainability', hashtags: ['#Sustainability', '#SDGs', '#GreenHeritage'] },
  { topic: 'Youth',          hashtags: ['#YouthEngagement', '#Młodzież', '#FutureLeaders'] },
  { topic: 'Partnership',    hashtags: ['#PartnerSpotlight', '#Współpraca'] },
];

function parseStringArray(raw) {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v) => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

function parseTopicArray(raw) {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((row) => row && typeof row === 'object' && typeof row.topic === 'string')
      .map((row) => ({
        topic: row.topic,
        hashtags: Array.isArray(row.hashtags) ? row.hashtags.filter((v) => typeof v === 'string') : [],
      }));
  } catch {
    return [];
  }
}

function ChipInput({ placeholder, onAdd }) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState('');

  function commit() {
    let value = draft.trim();
    if (!value) return;
    value = value.replace(/\s+/g, '');
    if (!value.startsWith('#')) value = '#' + value;
    onAdd(value);
    setDraft('');
  }

  return (
    <div className="chip-input">
      <input
        type="text"
        className="chip-input__field"
        value={draft}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          }
        }}
      />
      <button type="button" className="chip-input__add" onClick={commit} aria-label={t('common.add')}>+</button>
    </div>
  );
}

function HashtagChip({ value, variant, onRemove }) {
  const { t } = useTranslation();
  return (
    <span className={'chip' + (variant ? ` chip--${variant}` : '')}>
      <span>{value}</span>
      <button
        type="button"
        className="chip__remove"
        onClick={onRemove}
        aria-label={`${t('common.remove')} ${value}`}
      >
        ×
      </button>
    </span>
  );
}

export function HashtagsTab() {
  const { t } = useTranslation();
  const { status, setSaving, flashSaved, flashError } = useSaveIndicator();

  const [always, setAlways] = useState([]);
  const [byTopic, setByTopic] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [usingMockData, setUsingMockData] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    Promise.all([
      api.brand.get('hashtags_brand').catch(() => null),
      api.brand.get('hashtags_topic').catch(() => null),
    ])
      .then(([brandRes, topicRes]) => {
        if (cancelled) return;
        const brandValue = brandRes?.value;
        const topicValue = topicRes?.value;
        if (brandValue === undefined && topicValue === undefined) {
          // Both calls failed (network/mock fallback path).
          throw new Error('brand-config unreachable');
        }
        setAlways(parseStringArray(brandValue));
        setByTopic(parseTopicArray(topicValue));
        setUsingMockData(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('[hashtags] API unreachable, using mock data:', err.message);
        setAlways(MOCK_ALWAYS);
        setByTopic(MOCK_BY_TOPIC);
        setUsingMockData(true);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  // ── Persistence helpers (auto-save on every change) ──────────────────────
  async function persistAlways(next) {
    setSaving();
    try {
      await api.brand.set('hashtags_brand', JSON.stringify(next));
      flashSaved();
    } catch (err) {
      console.error('[hashtags] save brand failed:', err);
      flashError();
    }
  }

  async function persistByTopic(next) {
    setSaving();
    try {
      await api.brand.set('hashtags_topic', JSON.stringify(next));
      flashSaved();
    } catch (err) {
      console.error('[hashtags] save topic failed:', err);
      flashError();
    }
  }

  // ── Always-Use ───────────────────────────────────────────────────────────
  function addAlways(value) {
    if (always.includes(value)) return;
    const next = [...always, value];
    setAlways(next);
    persistAlways(next);
  }
  function removeAlways(value) {
    const next = always.filter((v) => v !== value);
    setAlways(next);
    persistAlways(next);
  }

  // ── By-Topic ─────────────────────────────────────────────────────────────
  function addToTopic(topic, value) {
    let changed = false;
    const next = byTopic.map((row) => {
      if (row.topic !== topic || row.hashtags.includes(value)) return row;
      changed = true;
      return { ...row, hashtags: [...row.hashtags, value] };
    });
    if (!changed) return;
    setByTopic(next);
    persistByTopic(next);
  }
  function removeFromTopic(topic, value) {
    const next = byTopic.map((row) =>
      row.topic === topic ? { ...row, hashtags: row.hashtags.filter((v) => v !== value) } : row,
    );
    setByTopic(next);
    persistByTopic(next);
  }
  function removeTopic(topic) {
    const next = byTopic.filter((row) => row.topic !== topic);
    setByTopic(next);
    persistByTopic(next);
  }

  if (isLoading) {
    return (
      <div className="empty-state">
        <div className="empty-state__icon" aria-hidden="true">⏳</div>
        <p>{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className="tab-page">
      {usingMockData && <div className="dev-notice">{t('events.loadError')}</div>}

      <section className="section-card">
        <h2 className="section-card__title">{t('brand.hashtags.alwaysUse')}</h2>
        <p className="section-card__hint">{t('brand.hashtags.alwaysUseHint')}</p>
        <div className="chip-section__chips" style={{ marginBottom: 'var(--space-3)' }}>
          {always.map((h) => (
            <HashtagChip key={h} value={h} variant="navy" onRemove={() => removeAlways(h)} />
          ))}
        </div>
        <ChipInput placeholder={t('brand.hashtags.placeholder')} onAdd={addAlways} />
      </section>

      <section className="section-card">
        <h2 className="section-card__title">{t('brand.hashtags.byTopic')}</h2>
        <p className="section-card__hint">{t('brand.hashtags.byTopicHint')}</p>
        <div className="topic-list">
          {byTopic.map((row) => (
            <div key={row.topic} className="topic-row">
              <div className="topic-row__header">
                <span className="topic-row__name">{row.topic}</span>
                <button
                  type="button"
                  className="data-table__action data-table__action--danger"
                  onClick={() => removeTopic(row.topic)}
                  aria-label={`${t('common.remove')} ${row.topic}`}
                  title={t('common.remove')}
                >
                  ✕
                </button>
              </div>
              <div className="topic-row__chips">
                {row.hashtags.map((h) => (
                  <HashtagChip key={h} value={h} onRemove={() => removeFromTopic(row.topic, h)} />
                ))}
              </div>
              <ChipInput
                placeholder={t('brand.hashtags.placeholder')}
                onAdd={(value) => addToTopic(row.topic, value)}
              />
            </div>
          ))}
        </div>
        {/* TODO: wire add-topic flow once we decide on the UX (modal vs inline). */}
        <button type="button" className="button button--ghost" style={{ marginTop: 'var(--space-3)' }} disabled>
          + {t('brand.common.addTopic')}
        </button>
      </section>

      <SaveToast status={status} />
    </div>
  );
}
